import DocumentIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Document.js';
import TrashIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Trash.js';
import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Box, useTheme } from '@mui/system';
import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { UploadArea } from 'src/components/atlas/molecules';
import { TextArea } from 'src/components/atoms';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import { casePageVm } from 'src/components/pages/Case/Case.slice';
import {
  CreateMitigationPlanInput,
  UploadSource,
  useCreateCaseDocumentationUploadUrlMutation,
  useCreateMitigationPlanMutation,
  useCreateMitigationPlanUploadMutation,
} from 'src/graphql';
import { FormState } from 'src/types';
import { uploadToPresignedUrl } from 'src/utils/uploadToPresignedUrl';
import { useTheme as useStyledTheme } from 'styled-components';
import { DrawerShell } from '../../molecules/Drawer/DrawerShell';
import * as styles from './DisclosuresApprovalDrawer.styles';

const MAX_FILE_SIZE_MB = 25;
const MAX_TOTAL_SIZE_MB = 100;
const SUPPORTED_FILE_TYPES = [
  '.docx',
  '.doc',
  '.pptx',
  '.ppt',
  '.xlsx',
  '.xls',
  '.pdf',
];

interface UploadedFile {
  file: File;
  uploadId: string;
}

export const DisclosuresApprovalDrawer = ({
  isOpen,
  onClose,
  caseId,
  refetch,
}: {
  isOpen: boolean;
  onClose: () => void;
  caseId: string;
  refetch: () => void;
}) => {
  const { tokens } = useTheme<LensTheme>();
  const styledTheme = useStyledTheme();
  const color = tokens.semantic.color;
  const sp = tokens.semantic.spacing.desktop.gap;

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [fileError, setFileError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<UploadedFile | null>(null);
  const [description, setDescription] = useState('');

  const reduxCase = useSelector(casePageVm);

  const getUploadUrl = useCreateCaseDocumentationUploadUrlMutation();
  const createUploadRecord = useCreateMitigationPlanUploadMutation();
  const createPlan = useCreateMitigationPlanMutation();

  const userHasEditAccess = useMemo(
    () =>
      reduxCase.permissions.canViewCaseWorkspaceTab &&
      reduxCase.permissions.canEditCaseWorkspaceTab,
    [
      reduxCase.permissions.canEditCaseWorkspaceTab,
      reduxCase.permissions.canViewCaseWorkspaceTab,
    ],
  );

  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      if (!files.length) return;

      setFileError('');

      const unsupported = files.find((f) => {
        const ext = f.name.slice(f.name.lastIndexOf('.')).toLowerCase();
        return !SUPPORTED_FILE_TYPES.includes(ext);
      });
      if (unsupported) {
        setFileError(
          `File "${unsupported.name}" is not a supported file type. Supported types: ${SUPPORTED_FILE_TYPES.join(', ')}.`,
        );
        return;
      }

      const oversized = files.find(
        (f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024,
      );
      if (oversized) {
        setFileError(
          `File "${oversized.name}" exceeds the ${MAX_FILE_SIZE_MB}MB limit.`,
        );
        return;
      }

      const currentTotal = uploadedFiles.reduce(
        (sum, { file }) => sum + file.size,
        0,
      );
      const newTotal = files.reduce((sum, f) => sum + f.size, 0) + currentTotal;
      if (newTotal > MAX_TOTAL_SIZE_MB * 1024 * 1024) {
        setFileError(`Total upload size cannot exceed ${MAX_TOTAL_SIZE_MB}MB.`);
        return;
      }

      setUploading(true);
      try {
        const results: UploadedFile[] = [];
        for (const file of files) {
          const { createCaseDocumentUploadURL } =
            await getUploadUrl.mutateAsync({
              type: file.type,
              filename: file.name,
              uploadSource: UploadSource.MitigationPlan,
              caseId,
              size: file.size,
            });

          const { url, s3path } = createCaseDocumentUploadURL;
          const resp = await uploadToPresignedUrl({
            file,
            url,
            tags: { uploadSource: UploadSource.MitigationPlan },
            onUploadProgress: () => {},
            controller: new AbortController(),
          });

          if (resp.status > 204) {
            if (results.length > 0) {
              setUploadedFiles((prev) => [...prev, ...results]);
            }
            setFileError(`Failed to upload "${file.name}".`);
            return;
          }

          const { createMitigationPlanUpload } =
            await createUploadRecord.mutateAsync({
              filename: file.name,
              s3path,
              size: file.size,
              mimeType: file.type,
            });

          results.push({ file, uploadId: createMitigationPlanUpload.id });
        }
        setUploadedFiles((prev) => [...prev, ...results]);
      } catch {
        setFileError(
          'An unexpected error occurred during upload. Please try again.',
        );
      } finally {
        setUploading(false);
      }
    },
    [uploadedFiles, getUploadUrl, createUploadRecord, caseId],
  );

  const handleRemoveFile = useCallback((uploadId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.uploadId !== uploadId));
  }, []);

  const handleSubmit = async () => {
    const input: CreateMitigationPlanInput = {
      caseId,
      title: 'Approved without mitigation plan',
      description: description.trim() || undefined,
      deadline: new Date().toISOString(),
      reminderFrequency: 0,
      reminderMaxCount: undefined,
      uploadIds: uploadedFiles.map((f) => f.uploadId),
      signatoryUserIds: [],
      noApprovalRequired: true,
    };

    try {
      await createPlan.mutateAsync({ data: input });
      onClose();
      refetch();
      handleSuccessToast({
        toastMessage: 'Disclosure approved successfully',
      });
    } catch {
      handleErrorToast({
        toastMessage: 'Failed to approve disclosure. Please try again.',
      });
    }
  };

  return (
    <>
      <DrawerShell
        testId="disclosures-approval-drawer"
        open={isOpen}
        title={'Approval'}
        onRequestClose={onClose}
        onNext={handleSubmit}
        nextDisabled={description.trim().length === 0}
        nextTitle={'Save'}
      >
        <Alert severity="warning" sx={{ marginBottom: sp[2].value }}>
          You are about to approve without a mitigation plan. Once approval is
          done, disclosure closes.
        </Alert>
        <Box>
          <Typography variant="body2" sx={styles.label}>
            Comment *
          </Typography>
          <TextArea
            data-testid="comment-textarea"
            rows={2}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
            }}
            state={FormState.DEFAULT}
          />
        </Box>

        <Box>
          <Typography variant="body2" sx={styles.label}>
            Additional documents
          </Typography>
          <Box>
            {userHasEditAccess && (
              <UploadArea
                supportedFormats={SUPPORTED_FILE_TYPES}
                isUploading={uploading}
                onFilesSelected={handleFilesSelected}
                maxFileSizeLabel="Max. file size: 25 MB / 100 MB in total for the plan"
              />
            )}

            {fileError && (
              <Typography
                variant="caption"
                color={color.action.destructive.default.value}
                data-testid="file-error"
              >
                {fileError}
              </Typography>
            )}

            {uploadedFiles.length > 0 && (
              <Box
                sx={{
                  marginTop: sp[2].value,
                  borderRadius: '8px',
                  border: `1px solid ${styledTheme.atlas.semantic.color.ui.divider.default}`,
                  padding: sp[2].value,
                }}
              >
                {uploadedFiles.map((uploadedFile) => {
                  return (
                    <Box
                      data-testid={`document-upload-${uploadedFile.uploadId}-row`}
                      key={uploadedFile.uploadId}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        '&:not(:last-child)': {
                          borderBottom: `1px solid ${styledTheme.atlas.semantic.color.ui.divider.default}`,
                          paddingBottom: sp[2].value,
                        },
                        '&:not(:first-child)': {
                          paddingTop: sp[2].value,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          color:
                            styledTheme.atlas.semantic.color.status.neutral
                              .text,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                        onClick={() => {}}
                      >
                        <DocumentIcon />
                        <Typography
                          variant="body2"
                          sx={{
                            color:
                              styledTheme.atlas.semantic.color.type.default,
                            fontSize: '16px',
                          }}
                        >
                          {uploadedFile.file.name}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: sp[2].value,
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFile(uploadedFile);
                            setShowConfirmDialog(true);
                          }}
                        >
                          <TrashIcon />
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Box>
      </DrawerShell>
      <Dialog
        open={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
        }}
      >
        <DialogTitle>Delete file</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to delete this file. <br /> If you continue, all
            changes will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Button
            data-testid="cancel-remove-document-confirm"
            variant="outlined"
            size="medium"
            onClick={() => {
              setShowConfirmDialog(false);
              setSelectedFile(null);
            }}
          >
            Cancel
          </Button>
          <Button
            data-testid="confirm-remove-documents"
            sx={{
              backgroundColor: color.action.destructive.default.value,
              color: color.action.destructive.onDestructive.value,
              '&:not(.Mui-disabled):hover': {
                backgroundColor: color.action.destructive.hover.value,
              },
            }}
            variant="contained"
            size="medium"
            onClick={() => {
              if (selectedFile) {
                handleRemoveFile(selectedFile.uploadId);
              }
              setShowConfirmDialog(false);
              setSelectedFile(null);
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
